<template>
  <tr class="jobseeker-row" :style="bgColor">
    <!-- 登録 / 更新日 -->
    <td class="td-date" @click="showProfile">
      <div class="date-container d-flex flex-column align-items-center text-center">
        <span>{{ joinedDate }}</span><br/>
        <CompanyFrom 
          v-if="jobseekerData.companyFrom === '自社'" 
        />
        <EventParticipation 
          v-if="jobseekerData.eventParticipation" 
        />
      </div>
    </td>

    <!-- 登録者情報 -->
    <td class="text-left registrant-info" @click="showProfile">
      <div class="td-container d-flex flex-column">
        <span
          class="jobseeker-name"
          @click="showProfile"
          v-html="getHighlightedContent(['full_name', 'last_name'], `${jobseekerData.last_name} ${jobseekerData.name}`)"
          style="cursor: pointer; font-weight: bold;"
        ></span>
        <span>{{ jobseekerData.age || 'N/A' }} / {{ employment.lastYearIncome || 'N/A' }}万円</span><br/>
        <span v-html="getHighlightedContent(['graduation_school_name'], jobseekerData.education && jobseekerData.education.instituteName || 'N/A')"/>
      </div>
    </td>

    <!-- 在籍企業 / 直近職種 -->
    <td class="text-left company-job-info" @click="showProfile">
      <div class="td-container d-flex flex-column">
        <span v-html="getHighlightedContent(['recent_registered_company_name'], employment.lastCompany || 'N/A')"></span><br/>
        <span>{{ employment.lastJob && employment.lastJob.title || 'N/A' }}/
          <br />
        ({{ employment.lastJob && employment.lastJob.child && employment.lastJob.child.title || 'N/A' }})</span>
      </div>
    </td>

    <!-- メモ -->
    <td class="text-center memo-cell">
      <div class="d-flex justify-content-center note-btn-grp">
        <Favorite
          :favorite="jobseekerData.isFavorite"
          @click.native="toggleFavorite(jobseekerData)"
          :toggledisabled="loading"
        />
        <span class="ml-2"></span>
        <Memo 
          v-bind:key="jobseekerData.id + (jobseekerData.memo || '')" 
          :applicant="jobseekerData"
        />
      </div>
    </td>

    <!-- ステータス -->
    <td class="text-center status-cell" :class="{ 'status-pending': jobseekerData.status === '未判定' }">
      {{ jobseekerData.status || 'N/A' }}
    </td>
  </tr>
</template>

<script>
import Favorite from '@/components/dashboard/applicants/Favorite.vue';
import Memo from '@/components/dashboard/applicants/Memo.vue';
import CompanyFrom from '@/components/dashboard/applicants/CompanyFrom.vue';
import EventParticipation from '@/components/dashboard/applicants/EventParticipation.vue';
import Api from '@/config/api';
import ApiService from '@/service/apiService';
import moment from 'moment';

export default {
  name: 'JobseekerRow',
  components: {
    Favorite,
    Memo,
    CompanyFrom,
    EventParticipation,
  },
  props: {
    jobseeker: {
      type: Object,
      required: true
    },
    jobseekerIndex: {
      type: Number,
      required: true
    },
    isPreview: {
      type: Boolean,
      default: false
    },
    isPendingReview: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      jobseekerData: this.jobseeker,
      loading: false,
    };
  },
  computed: {
    joinedDate() {
      return moment(this.jobseekerData.joinedDate)
        .utcOffset('+0900')
        .format('YYYY-MM-DD');
    },
    employment() {
      return this.jobseekerData.employment || {};
    },
    bgColor() {
      if (this.jobseekerData.profile_view_status === 'read') {
        return 'background-color: #D9D9D9';
      } else {
        return 'background-color: #fff';
      }
    },
  },
  methods: {
    getHighlightedContent(paths, originalText) {
      if (!this.jobseekerData.highlights || !this.jobseekerData.highlights.length) {
        return originalText;
      }

      // Special handling for name fields
      if (paths.includes('last_name') || paths.includes('full_name')) {
        let highlightedText = originalText;

        const nameHighlights = this.jobseekerData.highlights.filter(h => 
          paths.includes(h.path) && h.texts && h.texts.length
        );

        for (let highlight of nameHighlights) {
          const hitTexts = highlight.texts
            .filter(t => t.type === 'hit')
            .sort((a, b) => b.value.length - a.value.length);

          for (let hitText of hitTexts) {
            const searchRegex = new RegExp(this.escapeRegExp(hitText.value), 'gi');
            highlightedText = highlightedText.replace(
              searchRegex, 
              match => `<span class="highlight-hit">${match}</span>`
            );
          }
        }

        return highlightedText;
      }

      // Regular handling for other fields
      const highlights = this.jobseekerData.highlights.filter(h => paths.includes(h.path));
      if (highlights.length === 0) {
        return originalText;
      }

      let highlightedText = originalText;

      for (let highlight of highlights) {
        const hitTexts = highlight.texts
          .filter(t => t.type === 'hit')
          .sort((a, b) => b.value.length - a.value.length);

        for (let hitText of hitTexts) {
          const searchRegex = new RegExp(this.escapeRegExp(hitText.value), 'gi');
          highlightedText = highlightedText.replace(
            searchRegex, 
            match => `<span class="highlight-hit">${match}</span>`
          );
        }
      }

      return highlightedText;
    },
    escapeRegExp(string) {
      return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    },
    async toggleFavorite(jobseeker) {
      this.loading = true;
      try {
        const method = jobseeker.isFavorite ? 'delete' : 'put';
        const { data } = await ApiService[method](Api.endpoints.FAVORITE(jobseeker.id));
        // Update the jobseeker data
        this.jobseekerData = { ...this.jobseekerData, ...data.data };
      } catch (error) {
        console.error('Error toggling favorite:', error);
      } finally {
        this.loading = false;
      }
    },
    showProfile() {
      // Emit event to parent component instead of opening modal
      this.$emit('showProfile', this.jobseekerData);
      // Mark as read if needed
      if (this.jobseekerData.profile_view_status !== 'read') {
        this.markAsRead();
      }
    },
    async markAsRead() {
      try {
        const { data } = await ApiService.put(Api.endpoints.MARK_APPLICANT_READ(this.jobseekerData.id), {
          status: 'read'
        });
        if (data.data) {
          this.jobseekerData.profile_view_status = 'read';
        }
      } catch (error) {
        console.error('Error marking jobseeker as read:', error);
      }
    },
  },
  watch: {
    'jobseeker': {
      handler(newJobseeker) {
        const fieldsToUpdate = {
          highlights: newJobseeker.highlights,
          last_name: newJobseeker.last_name,
          name: newJobseeker.name,
          education: newJobseeker.education,
          employment: newJobseeker.employment,
          status: newJobseeker.status,
          isFavorite: newJobseeker.isFavorite,
          memo: newJobseeker.memo
        };
        
        Object.assign(this.jobseekerData, fieldsToUpdate);
      },
      deep: false
    },
  },
};
</script>

<style scoped>
.jobseeker-row {
  cursor: pointer;
}

.jobseeker-row:hover {
  background-color: #f8f9fa !important;
}

.td-date {
  min-width: 120px;
  max-width: 120px;
  padding: 8px;
}

.registrant-info {
  min-width: 150px;
  max-width: 150px;
  padding: 8px;
}

.company-job-info {
  min-width: 180px;
  max-width: 180px;
  padding: 8px;
}

.memo-cell {
  min-width: 100px;
  max-width: 100px;
  padding: 8px;
}

.status-cell {
  min-width: 100px;
  max-width: 100px;
  padding: 8px;
}

.status-pending {
  color: #ff6b6b;
  font-weight: bold;
}

.date-container {
  font-size: 0.9em;
}

.company-source-label {
  width: 20px;
  height: 20px;
  margin-top: 4px;
}

.jobseeker-name {
  color: #2c3e50;
  font-size: 1.1em;
}

.td-container {
  line-height: 1.4;
}

.note-btn-grp {
  gap: 8px;
}

.highlight-hit {
  background-color: #ffeb3b;
  padding: 2px 4px;
  border-radius: 3px;
  font-weight: bold;
}
</style> 